import { useEffect, useMemo, useState } from "react";
import { PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { RoleWithPermissions } from "@forgepoint/shared";
import { sortRoles, useRolesStore } from "@/store/rolesStore";
import { RoleList } from "./RoleList";
import { RoleEditSheet } from "./RoleEditSheet";

/**
 * Settings panel for managing organization roles: lists default and custom
 * roles, and lets admins create, edit, and delete custom roles via a sheet.
 * @returns Roles settings panel element
 */
export function RolesSettingsPanel() {
  const roleMap = useRolesStore((state) => state.roleMap);
  const isLoadingList = useRolesStore((state) => state.isLoadingList);
  const error = useRolesStore((state) => state.error);
  const fetchRoles = useRolesStore((state) => state.fetchRoles);
  const fetchCatalog = useRolesStore((state) => state.fetchCatalog);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
    fetchCatalog();
  }, [fetchRoles, fetchCatalog]);

  const roles = useMemo(() => sortRoles(roleMap), [roleMap]);
  const editingRole = editingRoleId
    ? (roleMap.get(editingRoleId) ?? null)
    : null;

  const handleCreate = () => {
    setEditingRoleId(null);
    setSheetOpen(true);
  };

  const handleEdit = (role: RoleWithPermissions) => {
    setEditingRoleId(role.id);
    setSheetOpen(true);
  };

  if (isLoadingList && roles.length === 0) {
    return (
      <div className="space-y-6 pt-4">
        <Skeleton className="h-5 w-24" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <Skeleton className="m-4 h-10" />
            <Skeleton className="m-4 h-10" />
          </div>
        </Card>
      </div>
    );
  }

  if (error && roles.length === 0) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold tracking-tight pl-2">Roles</h3>
            <p className="text-sm text-muted-foreground pl-2">
              Default roles are managed by the system. Create custom roles to
              grant users a specific set of permissions.
            </p>
          </div>
          <Button size="sm" onClick={handleCreate}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Create Role
          </Button>
        </div>

        <RoleList roles={roles} onEdit={handleEdit} />
      </div>

      <RoleEditSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        role={editingRole}
      />
    </>
  );
}
